import { useEffect, useState } from "react";

// Lado máximo del canvas en el que se analiza el logo: basta para detectar el fondo y evita leer imágenes enormes
const MAX_SAMPLE_SIZE = 256;
// Por debajo de este alfa un píxel se considera transparente (los bordes suavizados de un logo opaco no llegan)
const TRANSPARENT_ALPHA_THRESHOLD = 250;

// Resultado por URL: true (tiene transparencia), false (opaco) o null (no se ha podido analizar)
const cache = new Map();

/** URL del logo del tenant, tal y como la publica App.js en la variable CSS --logo. */
export const getLogoUrl = () => {
  const value = getComputedStyle(document.documentElement).getPropertyValue("--logo").trim();
  const match = value.match(/^url\(\s*["']?(.*?)["']?\s*\)$/);
  const url = match?.[1];
  return url && url !== "undefined" && url !== "null" ? url : null;
};

/**
 * Indica si la imagen tiene algún píxel transparente. Para leer los píxeles el servidor de la imagen debe
 * permitir CORS; si no lo permite (o la imagen no carga) el resultado es null.
 */
export const detectImageTransparency = (url) => {
  if (!url) return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        // Los SVG sin tamaño intrínseco cargan con 0x0
        const width = img.naturalWidth || MAX_SAMPLE_SIZE;
        const height = img.naturalHeight || MAX_SAMPLE_SIZE;
        const scale = Math.min(1, MAX_SAMPLE_SIZE / Math.max(width, height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < TRANSPARENT_ALPHA_THRESHOLD) return resolve(true);
        }
        resolve(false);
      } catch (e) {
        // Canvas contaminado (imagen sin cabeceras CORS)
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

  cache.set(url, promise);
  return promise;
};

/**
 * Transparencia del logo del tenant: undefined mientras se analiza, y después true, false o null
 * (ver detectImageTransparency).
 */
const useLogoHasTransparency = () => {
  const [hasTransparency, setHasTransparency] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    detectImageTransparency(getLogoUrl()).then((result) => {
      if (!cancelled) setHasTransparency(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return hasTransparency;
};

export default useLogoHasTransparency;
